import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createScheduledFollowUp,
  deleteScheduledFollowUp,
  escalateLead,
  fetchAiPrompts,
  fetchConversation,
  fetchLead,
  fetchMessage,
  fetchMessageMedia,
  fetchMessageMediaData,
  fetchMessageTemplates,
  invalidateLead,
  invalidPhoneLead,
  markLeadAsDone,
  noResponseNeededLead,
  patchLead,
  qualifyLead,
  reopenLead,
  runAiPrompt,
  stopContactLead,
  unqualifyLead,
} from 'services/api/lead.api.service';
import { continueAutomation } from 'services/api/script.api.service';
import {
  createNote,
  flagMessage,
  rateActivity,
  removeEmailIssue,
  removeMessageIssue,
  reportEmailIssue,
  reportMessageIssue,
  unflagMessage,
} from 'services/api/concierge.api.service';
import callApiService from 'services/api/call.api.service';

import { setLoading, setNotification } from 'state/app.state';
import {
  AiPrompt,
  AiPromptResponse,
  CallTypes,
  CommunicationTypes,
  ConciergeActions,
  Distribution,
  Lead,
  LeadIcon,
  LeadTag,
  LoadingStates,
  MessageTemplate,
  ScheduleCallPayload,
  SmsMedia,
  ThunkAPI,
  UnqualifyReason,
} from 'typings';
import { apiChangeAvailability } from 'state/common.actions';
import { apiErrorNotification } from 'utils/functions';

interface LeadState {
  entity: Lead | null;
  communications: any[];
  unqualifyReasons: UnqualifyReason[];
  aiPrompts: AiPrompt[];
  messageTemplates: MessageTemplate[];
  isSmsCapable: boolean;
  isEmailCapable: boolean;
  isDoneActionEligible: boolean;
  isDoneActionAutomatic: boolean;
  isMarkedDone: boolean;
  isLeadStatusChanged: boolean;
  communicationLoadingState: LoadingStates;
  defaultCommType: string;
  distribution: Distribution | null;
  additionalNote: string;
  isRunningAiPrompt: boolean;
  aiPromptResponse: AiPromptResponse | null;
}

interface LeadSelectorState {
  lead: LeadState;
}

/**
 * Asynchronous Actions (thunks)
 */
export const apiFetchLead = createAsyncThunk(
  'lead/apiFetchLead',
  async (leadId: string, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      return await fetchLead(leadId);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error, 'Failed to retrieve lead')));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiFetchConversation = createAsyncThunk(
  'lead/fetchConversation',
  async (leadId: string, thunkApi: ThunkAPI) => {
    try {
      return await fetchConversation(leadId);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    }
  }
);

export const apiFetchMessage = createAsyncThunk(
  'lead/fetchMessage',
  async (messageId: number, thunkApi: ThunkAPI) => {
    try {
      return await fetchMessage(messageId);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    }
  }
);

export const apiFetchMessageMedia = createAsyncThunk(
  'lead/fetchMessageMedia',
  async (messageId: number, thunkApi: ThunkAPI) => {
    try {
      const media = await fetchMessageMedia(messageId);
      const mediaWithData = media.map((media: SmsMedia, index: number) => {
        return media.needsData ? fetchMessageMediaData(messageId, index) : media;
      });
      return await Promise.all(mediaWithData);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    }
  }
);

export const apiContinueAutomation = createAsyncThunk(
  'lead/apiContinueAutomation',
  async (_, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      const leadId: string = thunkApi.getState().lead.entity?.id;
      const updatedLead = await continueAutomation(leadId);

      return updatedLead;
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiCreateNote = createAsyncThunk(
  'lead/apiCreateNote',
  async ({ type, note }: { type: string; note: string }, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      const leadId = thunkApi.getState().lead.entity?.id;
      return await createNote({ leadId, type, note });
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiCreateScheduledFollowUp = createAsyncThunk(
  'lead/apiCreateScheduledFollowUp',
  async (payload: Partial<Lead>, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      const leadId = thunkApi.getState().lead.entity?.id;
      return await createScheduledFollowUp(leadId, payload);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiDeleteScheduledFollowUp = createAsyncThunk(
  'lead/apiDeleteScheduledFollowUp',
  async (_, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      const leadId = thunkApi.getState().lead.entity?.id;
      return await deleteScheduledFollowUp(leadId);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiEscalateLead = createAsyncThunk(
  'lead/apiEscalateLead',
  async (_, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      const leadId = thunkApi.getState().lead.entity?.id;
      return await escalateLead(leadId);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiInvalidateLead = createAsyncThunk(
  'lead/apiInvalidateLead',
  async (additionalNote: string, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      const leadId = thunkApi.getState().lead.entity?.id;
      return await invalidateLead(leadId, { additionalNote });
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiInvalidPhoneLead = createAsyncThunk(
  'lead/apiInvalidPhoneLead',
  async ({ reason, autoDone }: { reason: string; autoDone?: boolean }, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      const leadId = thunkApi.getState().lead.entity?.id;
      return await invalidPhoneLead(leadId, { reason });
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiNoResponseNeeded = createAsyncThunk(
  'lead/apiNoResponseNeeded',
  async (_, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      const leadId = thunkApi.getState().lead.entity?.id;
      return await noResponseNeededLead(leadId);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiMarkLeadAsDone = createAsyncThunk(
  'lead/apiMarkLeadAsDone',
  async (_, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      const leadId: string = thunkApi.getState().lead.entity?.id;
      await markLeadAsDone(leadId);

      return null;
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiPatchLead = createAsyncThunk(
  'lead/apiPatchLead',
  async (payload: any, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      const leadId = thunkApi.getState().lead.entity?.id;
      return await patchLead(leadId, payload);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiQualifyLead = createAsyncThunk(
  'lead/apiQualifyLead',
  async ({ additionalNote }: { additionalNote: string }, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      const leadId = thunkApi.getState().lead.entity?.id;
      return await qualifyLead(leadId, { additionalNote });
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiReopenLead = createAsyncThunk(
  'lead/apiReopenLead',
  async (additionalNote: string, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      const leadId = thunkApi.getState().lead.entity?.id;
      return await reopenLead(leadId, { additionalNote });
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiStopContactLead = createAsyncThunk(
  'lead/apiStopContactLead',
  async (_, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      const leadId = thunkApi.getState().lead.entity?.id;
      return await stopContactLead(leadId);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiUnqualifyLead = createAsyncThunk(
  'lead/apiUnqualifyLead',
  async (
    { additionalNote, reasonUnqualified }: { additionalNote: string; reasonUnqualified: string },
    thunkApi: ThunkAPI
  ) => {
    try {
      thunkApi.dispatch(setLoading(true));
      const leadId = thunkApi.getState().lead.entity?.id;
      return await unqualifyLead(leadId, { additionalNote, reasonUnqualified });
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiFlagMessage = createAsyncThunk(
  'lead/apiFlagMessage',
  async (
    { messageId, isFlagged }: { messageId: number; isFlagged: boolean },
    thunkApi: ThunkAPI
  ) => {
    try {
      thunkApi.dispatch(setLoading(true));
      isFlagged ? await flagMessage(messageId) : await unflagMessage(messageId);

      return { messageId, isFlagged };
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiRateActivity = createAsyncThunk(
  'lead/apiRateActivity',
  async (
    { data, communicationId, type }: { data: any; communicationId: any; type: CommunicationTypes },
    thunkApi: ThunkAPI
  ) => {
    try {
      thunkApi.dispatch(setLoading(true));
      await rateActivity(data);

      return { data, communicationId, type };
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiReportIssue = createAsyncThunk(
  'lead/apiReportIssue',
  async (
    { communicationId, type }: { communicationId: number; type: CommunicationTypes },
    thunkApi: ThunkAPI
  ) => {
    try {
      thunkApi.dispatch(setLoading(true));

      switch (type) {
        case CommunicationTypes.EMAIL:
          await reportEmailIssue(communicationId);
          break;
        case CommunicationTypes.SMS:
          await reportMessageIssue(communicationId);
          break;
      }

      return { communicationId, type };
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiRemoveIssue = createAsyncThunk(
  'lead/apiRemoveIssue',
  async (
    { communicationId, type }: { communicationId: number; type: CommunicationTypes },
    thunkApi: ThunkAPI
  ) => {
    try {
      thunkApi.dispatch(setLoading(true));

      switch (type) {
        case CommunicationTypes.EMAIL:
          await removeEmailIssue(communicationId);
          break;
        case CommunicationTypes.SMS:
          await removeMessageIssue(communicationId);
          break;
      }

      return { communicationId, type };
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiScheduleCall = createAsyncThunk(
  'lead/scheduleCall',
  async (arg: { leadId: string; data: ScheduleCallPayload }, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      await callApiService.scheduleCall(arg.leadId, arg.data);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiFetchMessageTemplates = createAsyncThunk(
  'lead/fetchMessageTemplates',
  async (leadId: string, thunkApi: ThunkAPI) => {
    try {
      return fetchMessageTemplates(leadId);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    }
  }
);

export const apiFetchAiPrompts = createAsyncThunk(
  'concierge/apiFetchAiPrompts',
  async (leadId: string, thunkApi: ThunkAPI) => {
    try {
      return await fetchAiPrompts(leadId);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    }
  }
);

export const apiRunAiPrompt = createAsyncThunk(
  'lead/apiRunAiPrompt',
  async ({ aiPromptId }: { aiPromptId: string }, thunkApi: ThunkAPI) => {
    try {
      const leadId = thunkApi.getState().lead.entity?.id;
      return await runAiPrompt(aiPromptId, leadId);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    }
  }
);

/* Lead State Slice */
const leadInitialState: LeadState = {
  entity: null,
  communications: [],
  unqualifyReasons: [],
  aiPrompts: [],
  messageTemplates: [],
  isSmsCapable: false,
  isEmailCapable: false,
  isDoneActionEligible: false,
  isDoneActionAutomatic: false,
  isMarkedDone: false,
  isLeadStatusChanged: false,
  communicationLoadingState: LoadingStates.NOT_LOADED,
  defaultCommType: '',
  distribution: null,
  additionalNote: '',
  isRunningAiPrompt: false,
  aiPromptResponse: null,
};

const isDoneActionEligible = (leadEntity: Lead) => {
  const ineligibleTags = [
    LeadTag.URGENT,
    LeadTag.AWAITING_RESPONSE,
    LeadTag.ACTION_REQUIRED,
    LeadTag.INBOUND_CALL,
  ];

  const ineligibleIcons = [LeadIcon.CALL, LeadIcon.EMAIL, LeadIcon.GENERAL, LeadIcon.SMS];

  return (
    !ineligibleTags.some((tag) => leadEntity.tag === tag.toString()) ||
    !ineligibleIcons.some((icon) => leadEntity.icon === icon.toString())
  );
};

const applyLeadEntityUpdate = (state: LeadState, rawUpdates: any, force: boolean = false) => {
  if (!rawUpdates) return;

  let updates: any = {};

  if (force) {
    // force allows any updates
    updates = rawUpdates;
  } else {
    // otherwise, sanitize the updates
    const allowedKeys = [
      'additionalNote',
      'city',
      'country',
      'email',
      'firstName',
      'icon',
      'lastAction',
      'lastName',
      'leadComment',
      'leadType',
      'phone',
      'phoneCountryIso',
      'phoneCarrierType',
      'phoneCarrierName',
      'postalCode',
      'reasonUnqualified',
      'requestedCallback',
      'requestedFollowUp',
      'state',
      'status',
      'street',
      'tag',
      'timezone',
    ];

    const requiredNonEmptyKeys = ['icon', 'status', 'tag'];

    for (const key of allowedKeys) {
      // skip this key if it's not one we're updating
      if (!rawUpdates.hasOwnProperty(key)) continue;

      // skip this key if it's attempting to set an empty value to one that can't accept an empty value
      if (requiredNonEmptyKeys.includes(key) && !rawUpdates[key]) continue;

      updates[key] = rawUpdates[key];
    }
  }

  const updatedEntity = { ...state.entity, ...updates };
  state.entity = updatedEntity;
  state.isDoneActionEligible = isDoneActionEligible(updatedEntity);
};

const clearLead = (state: LeadState) => {
  state.isMarkedDone = true;
  state.isDoneActionEligible = false;
  state.isDoneActionAutomatic = false;
};

const updateCommunication = (state: LeadState, data: any, type: CommunicationTypes) => {
  state.communications = state.communications.map((communication: any) => {
    if (communication.commType === type && communication.id === data.id) {
      return { ...communication, ...data };
    }

    return communication;
  });
};

export const leadSlice = createSlice({
  name: 'lead',
  initialState: leadInitialState,
  reducers: {
    // used to make realtime changes to the lead payload fetched from the api (i.e. updates we listen for via pusher)
    updateLeadEntity: (state, action) => applyLeadEntityUpdate(state, action.payload),
    forceUpdateLeadEntity: (state, action) => applyLeadEntityUpdate(state, action.payload, true),
    updateLeadCommunications: (state, action) => {
      const communication = action.payload;
      state.communications.push(communication);
    },
    updateLeadSummaryStatus: (state, action: PayloadAction<boolean>) => {
      state.isLeadStatusChanged = action.payload;
    },
    clearAiPromptResponse: (state) => {
      state.aiPromptResponse = null;
    },
  },
  extraReducers: {
    /**
     * if the concierge marks as inactive, the lead is unassigned so
     * we set isMarkedDone to true so the lead is sent back to the home page
     */
    [apiChangeAvailability.fulfilled.toString()]: (state, action) => {
      if (action.payload?.isActive === false) {
        clearLead(state);
      }
    },
    [apiFetchLead.fulfilled.toString()]: (state, action) => {
      const lead = action.payload;

      state.entity = lead;
      state.distribution = lead.distribution;
      state.unqualifyReasons = lead.unqualifyReasons;

      if (lead) {
        state.isDoneActionEligible = isDoneActionEligible(lead);
      }
    },
    [apiFetchConversation.pending.toString()]: (state, action) => {
      state.communicationLoadingState = LoadingStates.LOADING;
    },
    [apiFetchConversation.rejected.toString()]: (state, action) => {
      state.communicationLoadingState = LoadingStates.NOT_LOADED;
    },
    [apiFetchConversation.fulfilled.toString()]: (state, action) => {
      state.communications = action.payload.communications;
      state.defaultCommType = action.payload.defaultCommType;
      state.communicationLoadingState = LoadingStates.LOADED;
    },
    [apiFetchMessage.fulfilled.toString()]: (state, action) => {
      const message = {
        ...action.payload,
        loadingState: LoadingStates.LOADED,
      };

      updateCommunication(state, message, CommunicationTypes.SMS);
    },
    [apiFetchMessageMedia.fulfilled.toString()]: (state, action) => {
      const messageId = action.meta.arg;
      const media = action.payload;

      state.communications = state.communications.map((communication: any) => {
        if (communication.commType === ConciergeActions.SMS && communication.id === messageId) {
          return { ...communication, media };
        }

        return communication;
      });
    },
    [apiFlagMessage.fulfilled.toString()]: (state, action) => {
      const updateFlaggedMessage = (array: any, update: any) => {
        const { isFlagged, messageId } = update.payload;
        return array.map((communication: any) => {
          if (communication.commType === ConciergeActions.SMS && communication.id === messageId) {
            return {
              ...communication,
              isFlagged,
            };
          }

          return communication;
        });
      };

      state.communications = updateFlaggedMessage(state.communications, action);
    },
    [apiRateActivity.fulfilled.toString()]: (state, action) => {
      const updateIsRated = (array: any, update: any) => {
        const { communicationId, type } = update.payload;
        return array.map((communication: any) => {
          if (communication.commType === type && communication.id === communicationId) {
            return {
              ...communication,
              isRated: true,
            };
          }

          return communication;
        });
      };

      state.communications = updateIsRated(state.communications, action);
    },
    [apiReportIssue.fulfilled.toString()]: (state, action) => {
      const updateHasIssue = (array: any, update: any) => {
        const { communicationId, type } = update.payload;
        return array.map((communication: any) => {
          if (communication.commType === type && communication.id === communicationId) {
            return {
              ...communication,
              hasIssue: true,
            };
          }

          return communication;
        });
      };

      state.communications = updateHasIssue(state.communications, action);
    },
    [apiRemoveIssue.fulfilled.toString()]: (state, action) => {
      const updateHasIssue = (array: any, update: any) => {
        const { communicationId, type } = update.payload;
        return array.map((communication: any) => {
          if (communication.commType === type && communication.id === communicationId) {
            return {
              ...communication,
              hasIssue: false,
            };
          }

          return communication;
        });
      };

      state.communications = updateHasIssue(state.communications, action);
    },
    [apiContinueAutomation.fulfilled.toString()]: (state, action) => {
      applyLeadEntityUpdate(state, action.payload);
      state.isDoneActionAutomatic = true;
    },
    [apiCreateScheduledFollowUp.fulfilled.toString()]: (state, action) => {
      state.isDoneActionAutomatic = true;
    },
    [apiDeleteScheduledFollowUp.fulfilled.toString()]: (state, action) => {
      applyLeadEntityUpdate(state, action.payload);
    },
    [apiEscalateLead.fulfilled.toString()]: (state, action) => {
      applyLeadEntityUpdate(state, action.payload);
      state.isDoneActionAutomatic = true;
    },
    [apiInvalidateLead.fulfilled.toString()]: (state, action) => {
      applyLeadEntityUpdate(state, action.payload);
      state.isDoneActionAutomatic = true;
    },
    [apiInvalidPhoneLead.fulfilled.toString()]: (state, action) => {
      applyLeadEntityUpdate(state, action.payload);
      state.isDoneActionAutomatic = action.meta.arg.autoDone ?? false;
    },
    [apiMarkLeadAsDone.pending.toString()]: (state) => clearLead(state),
    [apiNoResponseNeeded.fulfilled.toString()]: (state, action) => {
      applyLeadEntityUpdate(state, action.payload);
      state.isDoneActionAutomatic = true;
    },
    [apiPatchLead.fulfilled.toString()]: (state, action) => {
      applyLeadEntityUpdate(state, action.payload);
    },
    [apiQualifyLead.fulfilled.toString()]: (state, action) => {
      applyLeadEntityUpdate(state, action.payload);
      state.isDoneActionAutomatic = true;
    },
    [apiReopenLead.fulfilled.toString()]: (state, action) => {
      applyLeadEntityUpdate(state, action.payload);
    },
    [apiStopContactLead.fulfilled.toString()]: (state, action) => {
      applyLeadEntityUpdate(state, action.payload);
      state.isDoneActionAutomatic = true;
    },
    [apiUnqualifyLead.fulfilled.toString()]: (state, action) => {
      applyLeadEntityUpdate(state, action.payload);
      state.isDoneActionAutomatic = true;
    },
    [apiScheduleCall.fulfilled.toString()]: (state) => {
      state.isDoneActionAutomatic = true;
    },
    [apiFetchMessageTemplates.fulfilled.toString()]: (state, action) => {
      state.messageTemplates = action.payload?.data || [];
    },
    [apiFetchAiPrompts.fulfilled.toString()]: (state, action) => {
      state.aiPrompts = action.payload ?? [];
    },
    [apiRunAiPrompt.pending.toString()]: (state, action) => {
      state.isRunningAiPrompt = true;
    },
    [apiRunAiPrompt.fulfilled.toString()]: (state, action) => {
      state.aiPromptResponse = action.payload?.aiPromptResponse;
      state.isRunningAiPrompt = false;
    },
    [apiRunAiPrompt.rejected.toString()]: (state, action) => {
      state.isRunningAiPrompt = false;
    },
    'call/apiInitiateCallCenterFlow/fulfilled': (state) => {
      state.isDoneActionAutomatic = true;
    },
  },
});

export const {
  clearAiPromptResponse,
  forceUpdateLeadEntity,
  updateLeadEntity,
  updateLeadCommunications,
  updateLeadSummaryStatus,
} = leadSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state) => state.counter.value)`
export const selectLeadFullName = (state: any) =>
  `${state.lead.entity?.firstName || ''} ${state.lead.entity?.lastName || ''}`.trim();

export const selectLeadSmsCapable = (state: any) =>
  !!state.lead.entity?.isSmsCapable;

export const selectLeadEmailCapable = (state: any) =>
  !!state.lead.entity?.isEmailCapable;

export const selectLeadCallCenterRoutingEnabled = (state: LeadSelectorState): boolean => {
  return state.lead.entity?.agent?.agentSetting?.settings?.inboundCalls?.callCenterRouting?.enabled;
};

export const selectLeadCommunications = (state: LeadSelectorState) => {
  const isCallCenterRoutingEnabled = selectLeadCallCenterRoutingEnabled(state);
  const lead = state.lead;

  if (!isCallCenterRoutingEnabled) {
    // exclude `outbound-lead` call legs when lead is not for a call center routing customer
    return lead.communications.filter(({ commType, callType, recipientType }) => {
      if (commType !== CommunicationTypes.CALL || callType !== CallTypes.OUTBOUND) {
        return true;
      }

      return recipientType !== 'lead';
    });
  }

  return lead.communications;
};

export default leadSlice.reducer;
